import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import styles from "./Team.module.css";
import Spin from "../Spinner/Spinner";
import { API } from "../../config";
import { FaPlus } from "react-icons/fa";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { RxDragHandleHorizontal } from "react-icons/rx";

function SortableRow({ member }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: member._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr ref={setNodeRef} style={style} className={styles.teamrow}>
      <td>
        <DragHandle listeners={listeners} attributes={attributes} />
      </td>
      <td>
        {member.image && (
          <img src={member.image} alt={member.name} className={styles.avatar} />
        )}
      </td>
      <td>{member.name}</td>
      <td>{member.position}</td>
      <td className={styles.description}>{member.description}</td>
      <td className={styles.edit}>
        <Link to={`/team/${member._id}`}>
          <div>Edit</div>
        </Link>
      </td>
    </tr>
  );
}

const DragHandle = ({ listeners, attributes }) => (
  <span
    {...listeners}
    {...attributes}
    style={{
      cursor: "grab",
      padding: "8px",
      display: "inline-block",
      fontSize: "18px",
      userSelect: "none",
    }}
  >
    <RxDragHandleHorizontal />
  </span>
);

function Team() {
  const [members, setMembers] = useState([]);
  const [spin, setSpin] = useState(true);
  const [originalOrder, setOriginalOrder] = useState([]);
  const [isChanged, setIsChanged] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const getData = async () => {
    const res = await fetch(`${API}/Admin/team`);
    const json = await res.json();
    const data = json.members || json || [];
    setMembers(data);
    setOriginalOrder(data.map((m) => m._id));
    setSpin(false);
  };

  const updateOrder = async (order) => {
    try {
      await fetch(`${API}/Admin/team/reorder`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order }),
      });
    } catch (e) {
      console.error("Reorder failed:", e);
    }
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    setMembers((prev) => {
      const oldIndex = prev.findIndex((m) => m._id === active.id);
      const newIndex = prev.findIndex((m) => m._id === over.id);
      const newArray = arrayMove(prev, oldIndex, newIndex);
      newArray.forEach((item, idx) => {
        item.order = idx + 1;
      });
      const newOrderIds = newArray.map((m) => m._id);
      setIsChanged(
        JSON.stringify(newOrderIds) !== JSON.stringify(originalOrder)
      );
      return [...newArray];
    });
  };

  const handleSave = () => {
    const payload = members.map((m, idx) => ({ id: m._id, order: idx + 1 }));
    updateOrder(payload);
    setOriginalOrder(members.map((m) => m._id));
    setIsChanged(false);
  };

  useEffect(() => {
    getData();
  }, []);

  return (
    <>
      {spin ? (
        <Spin />
      ) : (
        <div className={styles.entirepage}>
          <div className={styles.upper}>
            <Link to="/">
              <span className={styles.swag}>
                Website <span className={styles.greater}>&gt;</span>
              </span>
            </Link>
            <span className={styles.req}>Team</span>
          </div>

          <div className={styles.heading}>Team</div>

          <div className={styles.topbar}>
            <div className={styles.addingbutton}>
              <Link to="/team/add">
                <div className={styles.button}>
                  <FaPlus className={styles.addicon} />
                  Add Member
                </div>
              </Link>
            </div>
            <button
              onClick={handleSave}
              disabled={!isChanged}
              className={styles.button}
              style={{ width: "fit-content" }}
            >
              Save Order
            </button>
          </div>

          <div className={styles.logosdisplay}>
            <table className={styles.displaytable}>
              <thead>
                <tr className={styles.teamrowhead}>
                  <th></th>
                  <th></th>
                  <th>Name</th>
                  <th>Position</th>
                  <th>Bio</th>
                  <th></th>
                </tr>
              </thead>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={members.map((m) => m._id)}
                  strategy={verticalListSortingStrategy}
                >
                  <tbody>
                    {members.map((m) => (
                      <SortableRow member={m} key={m._id} />
                    ))}
                  </tbody>
                </SortableContext>
              </DndContext>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

export default Team;
